/**
 * ACTIVITY-CORE — "History" sheet for an entity's details (product/
 * warehouse/location/operation): local history for that entity with
 * "Undo this change" per entry (compensating server-side write).
 * The base Dialog already integrates useBackDismiss (back closes the overlay).
 */
import { msg } from "@lingui/core/macro";
import { History } from "@fleet/icons";
import { type ActivityRow, getEntityActivityQuery, queryKeys } from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { ActivityList } from "./activity-list";

const ENTITY_HISTORY_LIMIT = 100;

interface EntityHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "product" | "warehouse" | "location" | "stock_picking";
  entityId: string;
}

export function EntityHistoryDialog(props: EntityHistoryDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const historyQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.entity(userId(), props.entityType, props.entityId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getEntityActivityQuery(dbInstance, props.entityType, props.entityId, {
            limit: ENTITY_HISTORY_LIMIT,
          })
        : null;
    },
    enabled: () => props.open && Boolean(drizzle()),
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg">
      <div class="flex max-h-[70vh] flex-col gap-4" data-testid="entity-history">
        <div class="flex items-center gap-3">
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <History class="h-4.5 w-4.5" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-lg font-semibold">{t(msg`History`)}</h2>
            <p class="text-xs text-muted-foreground">
              {t(msg`Every change on this item, newest first.`)}
            </p>
          </div>
        </div>
        <div class="min-h-0 overflow-y-auto pr-1">
          <ActivityList
            rows={() => historyQuery.data ?? []}
            isLoading={() => historyQuery.isFetching && (historyQuery.data ?? []).length === 0}
            currentUserId={userId}
            canRevert
          />
        </div>
      </div>
    </Dialog>
  );
}
